import { motion, useDragControls, useMotionValue } from "framer-motion";
import { Grid2x2, GripHorizontal, LayoutGrid } from "lucide-react";
import { useCallback, useEffect } from "react";
import { LiquidGlass } from "../ui/LiquidGlass";
import { COMPACT_THRESHOLD } from "./ImageCard";

type SizeSliderProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
};

const SLIDER_WIDTH = 180;
const STORAGE_KEY = "size-slider-pos";

function loadPosition(): { x: number; y: number } {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { x: 0, y: 0 };
  } catch {
    return { x: 0, y: 0 };
  }
}

export function SizeSlider({
  value,
  onChange,
  min = 30,
  max = 120,
}: SizeSliderProps) {
  const initial = loadPosition();
  const x = useMotionValue(initial.x);
  const y = useMotionValue(initial.y);
  const dragControls = useDragControls();

  const pct = ((value - min) / (max - min)) * 100;
  const thresholdPct = ((COMPACT_THRESHOLD - min) / (max - min)) * 100;
  const isCompact = value < COMPACT_THRESHOLD;

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value));
    },
    [onChange]
  );

  const persistPosition = useCallback(() => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ x: x.get(), y: y.get() })
    );
  }, [x, y]);

  useEffect(() => {
    return () => persistPosition();
  }, [persistPosition]);

  return (
    <motion.div
      animate={{ opacity: 1, scale: 1 }}
      className="fixed top-12 right-13 z-50 hidden md:block"
      drag
      dragControls={dragControls}
      dragElastic={0.05}
      dragListener={false}
      dragMomentum={false}
      initial={{ opacity: 0, scale: 0.95 }}
      onDragEnd={persistPosition}
      style={{ x, y }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
    >
      <LiquidGlass
        blurAmount={20}
        className="select-none"
        cornerRadius={16}
        padding="10px 14px"
        saturation={150}
      >
        <div className="flex items-center gap-3">
          <div
            className="cursor-grab active:cursor-grabbing"
            onPointerDown={(e) => dragControls.start(e)}
          >
            <GripHorizontal className="size-4 text-gray-600 transition-colors hover:text-gray-900 dark:text-white/50 dark:hover:text-white/80" />
          </div>

          <Grid2x2
            className={`size-4 transition-colors ${isCompact ? "text-selca-cocoa" : "text-gray-400 dark:text-white/30"}`}
          />

          <div className="flex items-center gap-2">
            <span className="min-w-7 rounded bg-white/10 px-1.5 py-0.5 text-center font-mono text-xs tabular-nums text-gray-900 dark:text-white/80">
              {value}
            </span>

            <div className="relative" style={{ width: SLIDER_WIDTH }}>
              <div className="absolute inset-y-0 left-0 my-auto h-1.5 w-full rounded-full bg-gray-200 dark:bg-white/10" />

              <div
                className="absolute inset-y-0 left-0 my-auto h-1.5 rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: isCompact
                    ? `linear-gradient(90deg, var(--vignette-color-1), var(--vignette-color-2))`
                    : `linear-gradient(90deg, var(--vignette-color-3), var(--vignette-color-5))`,
                }}
              />

              <div
                className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 rounded-full bg-gray-400/60 dark:bg-white/30 pointer-events-none"
                style={{ left: `${thresholdPct}%` }}
                title="Compact/Full threshold"
              />

              <input
                className="relative z-10 w-full cursor-pointer appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:size-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.3)] [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 dark:[&::-webkit-slider-thumb]:bg-white"
                max={max}
                min={min}
                onChange={handleSliderChange}
                type="range"
                value={value}
              />
            </div>

            <LayoutGrid
              className={`size-4 transition-colors ${!isCompact ? "text-selca-cocoa" : "text-gray-400 dark:text-white/30"}`}
            />
          </div>
        </div>
      </LiquidGlass>
    </motion.div>
  );
}
